/**
 * Employee Dashboard
 * Main page for employees to view tokens and submit requests
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import TokenMeter from '../components/TokenMeter';
import RequestCard from '../components/RequestCard';
import CalendarView from '../components/CalendarView';
import LoadingSpinner from '../components/LoadingSpinner';
import PasswordChangeForm from '../components/PasswordChangeForm';
import DateRangePicker from '../components/DateRangePicker';
import requestService from '../services/requestService';
import employeeService from '../services/employeeService';
import centerService from '../services/centerService';
import { evaluateAllRules } from '../utils/rulesEngine';
import { getConsecutiveDays, getDatesInRange, formatDate, dateRangesOverlap, getWorkingDays } from '../utils/dateHelpers';
import logger from '../utils/logger';
import auditService from '../services/auditService';
import dayjs from 'dayjs';

const EmployeeDashboard = () => {
  const { user, updateUser } = useAuth();
  const { requests, employees, settings, centers, refreshAll, loading: appLoading } = useApp();
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestForm, setRequestForm] = useState({
    startDate: '',
    endDate: '',
    reason: '',
    dateSelectionMode: 'simple',
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const errorMessageRef = useRef(null);
  
  const userRequests = requests.filter(req => req.employeeId === user?.id);
  
  // Get manager information
  const manager = useMemo(() => {
    if (!user?.managerId || !employees || employees.length === 0) {
      return null;
    }
    return employees.find(emp => emp.id === user.managerId);
  }, [user?.managerId, employees]);
  
  // Get blackout days for user's center
  const blackoutDays = useMemo(() => {
    console.log('EmployeeDashboard: Calculating blackout days', {
      userId: user?.id,
      userCenterId: user?.centerId,
      centersCount: centers?.length,
      centers: centers?.map(c => ({ id: c.id, name: c.name, blackoutDaysCount: c.blackoutDays?.length || 0 }))
    });
    
    if (!user?.centerId || !centers || centers.length === 0) {
      console.warn('EmployeeDashboard: No center blackout days - user has no centerId or centers not loaded', {
        userId: user?.id,
        centerId: user?.centerId,
        centersCount: centers?.length
      });
      logger.debug('No center blackout days - user has no centerId or centers not loaded', {
        userId: user?.id,
        centerId: user?.centerId,
        centersCount: centers?.length
      });
      return [];
    }
    const userCenter = centers.find(c => c.id === user.centerId);
    
    if (!userCenter) {
      console.warn('EmployeeDashboard: User center not found', {
        userId: user.id,
        centerId: user.centerId,
        availableCenters: centers.map(c => c.id)
      });
      return [];
    }
    
    const centerBlackoutDays = userCenter?.blackoutDays || [];
    console.log('EmployeeDashboard: Found user center', {
      centerId: userCenter.id,
      centerName: userCenter.name,
      blackoutDaysRaw: centerBlackoutDays,
      blackoutDaysCount: centerBlackoutDays.length
    });
    
    // Ensure all dates are in YYYY-MM-DD format
    const normalizedBlackoutDays = centerBlackoutDays
      .filter(date => date != null) // Filter out null/undefined
      .map(date => {
        // Convert to string and trim whitespace
        const dateStr = String(date).trim();
        
        // If date is already in YYYY-MM-DD format, return as is
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
          return dateStr;
        }
        // Try to parse and format the date
        const parsed = dayjs(dateStr);
        if (parsed.isValid()) {
          return parsed.format('YYYY-MM-DD');
        }
        logger.warning('Invalid blackout date format in center', { 
          centerId: user.centerId, 
          date, 
          dateStr 
        });
        return null;
      })
      .filter(date => date !== null);
    
    logger.info('Center blackout days loaded', {
      userId: user.id,
      centerId: user.centerId,
      centerName: userCenter?.name,
      blackoutDaysCount: normalizedBlackoutDays.length,
      blackoutDays: normalizedBlackoutDays,
      originalBlackoutDays: centerBlackoutDays
    });
    
    // Also log to console for debugging
    if (normalizedBlackoutDays.length > 0) {
      console.log('EmployeeDashboard: Center blackout days', {
        centerId: user.centerId,
        centerName: userCenter?.name,
        blackoutDays: normalizedBlackoutDays
      });
    } else {
      console.log('EmployeeDashboard: No blackout days found', {
        centerId: user.centerId,
        centerName: userCenter?.name,
        hasCenter: !!userCenter,
        originalBlackoutDays: centerBlackoutDays
      });
    }
    
    return normalizedBlackoutDays;
  }, [user?.id, user?.centerId, centers]);
  
  // Calculate min and max dates based on settings
  // Always start from today (or today + minAdvanceNoticeDays if configured)
  const minDate = useMemo(() => {
    if (!settings) return dayjs().format('YYYY-MM-DD');
    const minAdvance = settings.minAdvanceNoticeDays || 0;
    // Ensure we don't go before today
    const calculatedDate = dayjs().add(minAdvance, 'day');
    const today = dayjs();
    return calculatedDate.isBefore(today) ? today.format('YYYY-MM-DD') : calculatedDate.format('YYYY-MM-DD');
  }, [settings]);
  
  const maxDate = useMemo(() => {
    if (!settings) return dayjs().add(365, 'day').format('YYYY-MM-DD');
    const maxAdvance = settings.maxAdvanceBookingDays || 365;
    return dayjs().add(maxAdvance, 'day').format('YYYY-MM-DD');
  }, [settings]);
  
  useEffect(() => {
    logger.info('EmployeeDashboard mounted', { userId: user?.id });
    refreshAll();
  }, [user, refreshAll]);

  // Auto-scroll to error messages when they appear
  useEffect(() => {
    if ((validationErrors.rules || validationErrors.dates || validationErrors.reason || validationErrors.submit) && errorMessageRef.current) {
      errorMessageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [validationErrors]);
  
  const handleRequestSubmit = async (e) => {
    e.preventDefault();
    setValidationErrors({});
    setSubmitting(true);
    
    try {
      logger.info('Submitting token request', { userId: user.id, requestForm });
      
      // Validate dates
      if (!requestForm.startDate || !requestForm.endDate) {
        setValidationErrors({ dates: 'Please select both start and end dates' });
        setSubmitting(false);
        return;
      }
      
      // Validate reason is mandatory
      if (!requestForm.reason || !requestForm.reason.trim()) {
        setValidationErrors({ reason: 'Reason is required and cannot be blank' });
        setSubmitting(false);
        return;
      }
      
      const consecutiveDays = getConsecutiveDays(requestForm.startDate, requestForm.endDate);
      if (consecutiveDays < 1) {
        setValidationErrors({ dates: 'End date must be after start date' });
        setSubmitting(false);
        return;
      }
      
      // Get all dates in range for validation
      const datesInRange = getDatesInRange(requestForm.startDate, requestForm.endDate);
      
      // Validation 1: Check for blackout days
      const blackoutConflicts = datesInRange.filter(date => blackoutDays.includes(date));
      
      if (blackoutConflicts.length > 0) {
        const conflictList = blackoutConflicts.map(date => formatDate(date, 'MMM DD, YYYY')).join(', ');
        setValidationErrors({ 
          dates: `The following dates are blackout days for your center and cannot be requested: ${conflictList}` 
        });
        setSubmitting(false);
        return;
      }
      
      // Validation 2: Check for duplicate/overlapping existing requests (approved or pending)
      const pendingOrApprovedRequests = userRequests.filter(req => 
        req.status === 'approved' || req.status === 'pending'
      );
      
      const overlappingRequest = pendingOrApprovedRequests.find(req =>
        dateRangesOverlap(requestForm.startDate, requestForm.endDate, req.startDate, req.endDate)
      );
      
      if (overlappingRequest) {
        const existingStart = formatDate(overlappingRequest.startDate, 'MMM DD, YYYY');
        const existingEnd = formatDate(overlappingRequest.endDate, 'MMM DD, YYYY');
        const status = overlappingRequest.status === 'pending' ? 'Pending' : 'Approved';
        setValidationErrors({ 
          dates: `You already have a ${status} request for ${existingStart} to ${existingEnd}. You cannot apply for overlapping dates.` 
        });
        setSubmitting(false);
        return;
      }
      
      // Calculate working days (excluding weekends) - weekends are allowed but not counted
      const workingDays = getWorkingDays(requestForm.startDate, requestForm.endDate);
      
      // Validation 3: Prevent weekend-only requests
      if (workingDays === 0) {
        setValidationErrors({ 
          dates: 'You cannot apply for weekend-only dates. Please include at least one working day (Monday-Friday) in your request.' 
        });
        setSubmitting(false);
        return;
      }
      
      // Create request object
      const requestData = {
        employeeId: user.id,
        managerId: user.managerId,
        startDate: requestForm.startDate,
        endDate: requestForm.endDate,
        days: workingDays,
        reason: requestForm.reason.trim(),
      };
      
      // Evaluate rules
      const ruleResult = await evaluateAllRules(requestData);
      
      if (!ruleResult.passed) {
        const errorMessages = ruleResult.results
          .filter(r => !r.passed)
          .map(r => r.message)
          .join('; ');
        setValidationErrors({ rules: errorMessages });
        logger.warning('Request validation failed', { ruleResult });
        setSubmitting(false);
        return;
      }
      
      // Create request
      const newRequest = await requestService.createRequest(requestData);
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'create',
        entityType: 'request',
        entityId: newRequest.id,
        details: requestData,
      });
      
      // Refresh data
      await refreshAll();
      
      // Reset form
      setRequestForm({ startDate: '', endDate: '', reason: '', dateSelectionMode: 'simple' });
      setShowRequestForm(false);
      
      logger.info('Token request created successfully', { requestId: newRequest.id });
    } catch (error) {
      logger.error('Error creating request', error, { userId: user.id });
      setValidationErrors({ submit: error.message || 'Failed to create request' });
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleCancelRequest = async (request) => {
    const reason = prompt('Please provide a reason for cancellation (optional):');
    if (reason === null) return; // User cancelled
    
    setSubmitting(true);
    try {
      logger.info('Requesting cancellation', { requestId: request.id, userId: user.id });
      
      await requestService.requestCancellation(request.id, reason || '');
      
      // Log audit
      await auditService.logAudit({
        userId: user.id,
        userRole: user.role,
        action: 'request_cancellation',
        entityType: 'request',
        entityId: request.id,
        details: { reason },
      });
      
      await refreshAll();
      logger.info('Cancellation requested successfully', { requestId: request.id });
      alert('Cancellation request submitted. Waiting for manager approval.');
    } catch (error) {
      logger.error('Error requesting cancellation', error, { requestId: request.id });
      alert('Failed to request cancellation: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };
  
  if (appLoading && !submitting) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" text="Loading dashboard..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      {submitting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm">
          <div className="glass p-8">
            <LoadingSpinner size="lg" text="Submitting request..." />
          </div>
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-primary-500 mb-2">
                Welcome back, {user?.name}!
              </h1>
              <p className="text-gray-600 text-lg">Request time to work on innovation projects</p>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <div className="bg-white rounded-xl shadow-soft px-6 py-3 border border-gray-100">
                <div className="text-sm text-gray-500">Remaining Tokens</div>
                <div className="text-2xl font-bold text-primary-500">
                  {((user?.yearlyTokens || 12) - (user?.tokensUsed || 0))}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Token Usage Card */}
        <div className="bg-gradient-to-br from-white to-primary-50 rounded-2xl shadow-soft p-8 mb-6 border border-primary-100 animate-slide-up">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-primary-500 rounded-xl flex items-center justify-center shadow-lg">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Token Usage</h2>
            </div>
            <button
              onClick={() => setShowPasswordForm(!showPasswordForm)}
              className="px-4 py-2 text-sm font-semibold text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-all duration-200 flex items-center space-x-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>{showPasswordForm ? 'Cancel' : 'Change Password'}</span>
            </button>
          </div>
          {showPasswordForm ? (
            <div className="bg-white rounded-xl p-6 border border-gray-200">
              <h3 className="text-lg font-bold text-gray-800 mb-4">Change Password</h3>
              <PasswordChangeForm
                onSuccess={() => {
                  setShowPasswordForm(false);
                  alert('Password changed successfully!');
                }}
                onCancel={() => setShowPasswordForm(false)}
              />
            </div>
          ) : (
            <TokenMeter
              tokensUsed={user?.tokensUsed || 0}
              yearlyTokens={user?.yearlyTokens || 12}
              size="lg"
            />
          )}
        </div>
        
        {/* Request Form Card */}
        <div className="bg-white rounded-2xl shadow-soft p-8 mb-6 border border-gray-100 animate-slide-up">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Submit New Request</h2>
            </div>
            <button
              onClick={() => setShowRequestForm(!showRequestForm)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-200 transform hover:scale-105 ${
                showRequestForm
                  ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  : 'bg-primary-500 text-white shadow-lg hover:shadow-xl hover:bg-primary-600'
              }`}
            >
              {showRequestForm ? (
                <span className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Cancel</span>
                </span>
              ) : (
                <span className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  <span>New Request</span>
                </span>
              )}
            </button>
          </div>
          
          {showRequestForm && (
            <form onSubmit={handleRequestSubmit} className="space-y-6 animate-slide-up border-t border-gray-100 pt-6">
              {/* Manager Info */}
              {manager ? (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                      {manager.name?.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-blue-600 font-semibold mb-1">Request will be sent to:</p>
                      <p className="text-lg font-bold text-gray-900 truncate">{manager.name}</p>
                      <p className="text-sm text-gray-600 truncate">{manager.email}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <svg className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-yellow-800">No Manager Assigned</p>
                      <p className="text-xs text-yellow-700 mt-1">Please contact your administrator to assign a manager to your account.</p>
                    </div>
                  </div>
                </div>
              )}
              
              {(validationErrors.rules || validationErrors.dates || validationErrors.reason || validationErrors.submit) && (
                <div ref={errorMessageRef} className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-r-lg">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <div>
                      {validationErrors.rules && <p className="font-medium">{validationErrors.rules}</p>}
                      {validationErrors.dates && <p className="font-medium">{validationErrors.dates}</p>}
                      {validationErrors.reason && <p className="font-medium">{validationErrors.reason}</p>}
                      {validationErrors.submit && <p className="font-medium">{validationErrors.submit}</p>}
                    </div>
                  </div>
                </div>
              )}
              
              <div className="space-y-4">
                {/* Date Selection Mode Tabs */}
                <div className="flex space-x-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setRequestForm({ ...requestForm, dateSelectionMode: 'simple' })}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                      requestForm.dateSelectionMode !== 'calendar'
                        ? 'bg-primary-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Simple Input
                  </button>
                  <button
                    type="button"
                    onClick={() => setRequestForm({ ...requestForm, dateSelectionMode: 'calendar' })}
                    className={`flex-1 px-4 py-2 rounded-lg font-medium transition-all ${
                      requestForm.dateSelectionMode === 'calendar'
                        ? 'bg-primary-500 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    Calendar Picker
                  </button>
                </div>

                {/* Simple Date Input Mode */}
                {requestForm.dateSelectionMode !== 'calendar' && (
                  <div className="space-y-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Start Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={requestForm.startDate}
                          onChange={(e) => {
                            const newStartDate = e.target.value;
                            setRequestForm({ ...requestForm, startDate: newStartDate });
                            // Auto-clear end date if it's before new start date
                            if (requestForm.endDate && newStartDate > requestForm.endDate) {
                              setRequestForm(prev => ({ ...prev, startDate: newStartDate, endDate: '' }));
                            }
                          }}
                          min={minDate}
                          max={maxDate}
                          className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          End Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          value={requestForm.endDate}
                          onChange={(e) => setRequestForm({ ...requestForm, endDate: e.target.value })}
                          min={requestForm.startDate || minDate}
                          max={maxDate}
                          className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200 transition-all duration-200"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Calendar Picker Mode */}
                {requestForm.dateSelectionMode === 'calendar' && (
                  <div className="flex justify-center">
                    <DateRangePicker
                      startDate={requestForm.startDate}
                      endDate={requestForm.endDate}
                      onStartDateChange={(date) => setRequestForm({ ...requestForm, startDate: date })}
                      onEndDateChange={(date) => setRequestForm({ ...requestForm, endDate: date })}
                      minDate={minDate}
                      maxDate={maxDate}
                      blackoutDays={blackoutDays}
                      className="max-w-md"
                    />
                  </div>
                )}

                {/* Selected Range Summary */}
                {(requestForm.startDate || requestForm.endDate) && (
                  <div className="bg-primary-50 border-2 border-primary-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-2 font-medium">✓ Selected Range Confirmed:</p>
                        <p className="font-semibold text-primary-700 text-base">
                          {requestForm.startDate 
                            ? dayjs(requestForm.startDate).format('MMMM D, YYYY') 
                            : 'Start date not selected'}
                          {' → '}
                          {requestForm.endDate 
                            ? dayjs(requestForm.endDate).format('MMMM D, YYYY') 
                            : 'End date not selected'}
                        </p>
                        {requestForm.startDate && requestForm.endDate && (
                          <p className="text-sm text-gray-600 mt-2 font-medium">
                            Duration: <span className="font-bold text-primary-700">{getWorkingDays(requestForm.startDate, requestForm.endDate)}</span> working day(s)
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setRequestForm({ ...requestForm, startDate: '', endDate: '' })}
                        className="px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-800 hover:bg-primary-100 rounded-lg transition-colors whitespace-nowrap"
                      >
                        Change Dates
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              
              {/* Dates Required Hint */}
              {(!requestForm.startDate || !requestForm.endDate) && (
                <div className="bg-blue-50 border-l-4 border-blue-400 text-blue-700 px-4 py-3 rounded-r-lg">
                  <p className="text-sm font-medium">
                    📅 Please select both start and end dates above to continue
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={requestForm.reason}
                  onChange={(e) => {
                    setRequestForm({ ...requestForm, reason: e.target.value });
                    // Clear error when user starts typing
                    if (validationErrors.reason) {
                      setValidationErrors({ ...validationErrors, reason: null });
                    }
                  }}
                  rows={4}
                  required
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200 bg-gray-50 focus:bg-white resize-none ${
                    validationErrors.reason ? 'border-red-500' : 'border-gray-200'
                  }`}
                  placeholder="Describe the purpose of your innovation token request (required)..."
                />
                {validationErrors.reason && (
                  <p className="text-sm text-red-600 mt-1">{validationErrors.reason}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  This field is required and cannot be left blank.
                </p>
              </div>
              
              <button
                type="submit"
                disabled={submitting || !requestForm.startDate || !requestForm.endDate}
                className={`w-full px-6 py-3.5 text-white font-semibold rounded-xl shadow-lg transition-all duration-200 focus:outline-none flex items-center justify-center space-x-2 ${
                  !requestForm.startDate || !requestForm.endDate
                    ? 'bg-gray-400 cursor-not-allowed opacity-60'
                    : 'bg-gradient-to-r from-green-500 to-emerald-600 hover:shadow-xl transform hover:scale-[1.02] focus:ring-4 focus:ring-green-300'
                } ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {submitting ? (
                  <>
                    <LoadingSpinner size="sm" text="" />
                    <span className="ml-2">Submitting...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{!requestForm.startDate || !requestForm.endDate ? 'Select Both Dates' : 'Submit Request'}</span>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
        
        {/* Calendar View */}
        <div className="glass p-8 mb-6 animate-slide-up">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-xl flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Calendar View</h2>
          </div>
          <CalendarView 
            requests={userRequests} 
            employees={employees} 
            centers={centers}
            settings={settings}
            userCenterId={user?.centerId}
          />
        </div>
        
        {/* Request History */}
        <div className="glass p-8 animate-slide-up">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-500 rounded-xl flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-gray-800">Request History</h2>
            </div>
            <div className="px-4 py-2 bg-primary-50 text-primary-700 rounded-lg font-semibold text-sm">
              {userRequests.length} {userRequests.length === 1 ? 'Request' : 'Requests'}
            </div>
          </div>
          <div className="space-y-4">
            {userRequests.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <p className="text-gray-500 text-lg font-medium">No requests yet</p>
                <p className="text-gray-400 text-sm mt-2">Submit your first innovation token request above</p>
              </div>
            ) : (
              userRequests
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .map(request => {
                  const employee = employees.find(emp => emp.id === request.employeeId);
                  return (
                    <RequestCard
                      key={request.id}
                      request={request}
                      employee={employee}
                      employees={employees}
                      onView={(req) => setSelectedRequest(req)}
                      onCancel={handleCancelRequest}
                      showCancelButton={true}
                    />
                  );
                })
            )}
          </div>
        </div>
        
        {/* Request Details Modal */}
        {selectedRequest && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-md flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100">
              <div className="sticky top-0 bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 flex items-center justify-between border-b border-primary-400">
                <h2 className="text-2xl font-bold text-white">Request Details</h2>
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="text-white hover:bg-primary-700 rounded-lg p-2 transition-all"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* Employee Info */}
                <div className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl border border-gray-200">
                  <div className="w-16 h-16 bg-primary-500 rounded-xl flex items-center justify-center text-white text-2xl font-bold shadow-md">
                    {employees.find(emp => emp.id === selectedRequest.employeeId)?.name?.charAt(0).toUpperCase() || '?'}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-800">
                      {employees.find(emp => emp.id === selectedRequest.employeeId)?.name || 'Unknown Employee'}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {employees.find(emp => emp.id === selectedRequest.employeeId)?.email || ''}
                    </p>
                  </div>
                </div>
                
                {/* Manager Info */}
                {(() => {
                  const employee = employees.find(emp => emp.id === selectedRequest.employeeId);
                  const manager = employee?.managerId ? employees.find(emp => emp.id === employee.managerId) : null;
                  
                  return (
                    <div className="flex items-center space-x-4 p-4 bg-blue-50 rounded-xl border border-blue-200">
                      <div className="flex-shrink-0">
                        <svg className="w-12 h-12 text-blue-600 bg-blue-100 rounded-lg p-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold mb-1">Request Raised To</p>
                        {manager ? (
                          <div>
                            <h3 className="text-lg font-bold text-blue-900">{manager.name}</h3>
                            <p className="text-sm text-blue-700">{manager.email}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-blue-600 italic">No manager assigned</p>
                        )}
                      </div>
                    </div>
                  );
                })()}
                
                {/* Status */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 border border-gray-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">Status</p>
                    <p className={`text-lg font-bold px-3 py-1 rounded-lg inline-block ${
                      selectedRequest.status === 'approved' ? 'bg-green-100 text-green-800' :
                      selectedRequest.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      selectedRequest.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                      selectedRequest.status === 'cancellation_requested' ? 'bg-orange-100 text-orange-800' :
                      selectedRequest.status === 'cancelled' ? 'bg-gray-100 text-gray-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedRequest.status.replace(/_/g, ' ').toUpperCase()}
                    </p>
                  </div>
                  <div className="p-4 border border-gray-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-1">Duration</p>
                    <p className="text-lg font-bold text-primary-600">
                      {selectedRequest.days || 1} {selectedRequest.days === 1 ? 'day' : 'days'}
                    </p>
                  </div>
                </div>
                
                {/* Dates */}
                <div className="p-4 border border-gray-200 rounded-xl">
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-3">Dates</p>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 font-medium">Start Date:</span>
                      <span className="text-gray-800 font-semibold">{formatDate(selectedRequest.startDate)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600 font-medium">End Date:</span>
                      <span className="text-gray-800 font-semibold">{formatDate(selectedRequest.endDate)}</span>
                    </div>
                  </div>
                </div>
                
                {/* Reason */}
                {selectedRequest.reason && (
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-blue-600 font-semibold mb-2">Reason</p>
                    <p className="text-gray-800">{selectedRequest.reason}</p>
                  </div>
                )}
                
                {/* Rejection Reason */}
                {selectedRequest.status === 'rejected' && selectedRequest.rejectionReason && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-red-600 font-semibold mb-2">Rejection Reason</p>
                    <p className="text-gray-800">{selectedRequest.rejectionReason}</p>
                  </div>
                )}
                
                {/* Cancellation Reason */}
                {selectedRequest.cancellationReason && (
                  <div className="p-4 bg-orange-50 border border-orange-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-orange-600 font-semibold mb-2">Cancellation Reason</p>
                    <p className="text-gray-800">{selectedRequest.cancellationReason}</p>
                  </div>
                )}
                
                {/* Review Info */}
                {selectedRequest.reviewedAt && (
                  <div className="p-4 bg-gray-50 border border-gray-200 rounded-xl">
                    <p className="text-xs uppercase tracking-wider text-gray-500 font-semibold mb-2">Reviewed</p>
                    <p className="text-gray-800">
                      <span className="font-semibold">{formatDate(selectedRequest.reviewedAt)}</span> by {employees.find(emp => emp.id === selectedRequest.reviewedBy)?.name || selectedRequest.reviewedBy || 'Manager'}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 flex justify-end">
                <button
                  onClick={() => setSelectedRequest(null)}
                  className="px-6 py-2.5 bg-primary-500 text-white font-semibold rounded-lg hover:bg-primary-600 transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeDashboard;

